/**
 * Particle property interpolation helpers.
 *
 * All animated properties use `PropertyFn<T> = (elapsed, ttl) => T`, where:
 *   - elapsed: milliseconds since the particle was created
 *   - ttl: the particle's total lifetime in milliseconds
 *
 * Each helper is a factory that returns a PropertyFn. In a ParticlePreset,
 * the factory is called once per particle to bake randomized initial values
 * into the closure — so `randomBetween()` inside a preset factory produces
 * a different value for each particle.
 */

export type PropertyFn<T> = (elapsed: number, ttl: number) => T;

// ── Numeric helpers ──

/**
 * Always returns the same value, ignoring elapsed/ttl.
 *   constant(1.0)  →  always 1.0
 */
export function constant(value: number): PropertyFn<number> {
    return () => value;
}

/**
 * Linear interpolation from `start` to `end` over the particle's lifetime.
 *   lerp(0.5, 0.1)  →  0.5 at birth, 0.3 at 50%, 0.1 at death
 */
export function lerp(start: number, end: number): PropertyFn<number> {
    return (elapsed, ttl) => {
        const t = elapsed / ttl;
        return start + (end - start) * t;
    };
}

/**
 * Ease-out interpolation (decelerating curve) from `start` to `end`.
 * Changes fast at first, then slows down. Uses quadratic easing: 1 - (1-t)^2.
 *   easeOut(1.0, 0.0)  →  1.0 at birth, ~0.75 at 50%, 0.0 at death
 */
export function easeOut(start: number, end: number): PropertyFn<number> {
    return (elapsed, ttl) => {
        const t = elapsed / ttl;
        const eased = 1 - (1 - t) * (1 - t);
        return start + (end - start) * eased;
    };
}

/**
 * Holds a fixed value until `holdUntil` fraction of lifetime, then delegates
 * to `thenFn` for the remaining time. `thenFn` receives re-normalized
 * elapsed/ttl so it runs its full range in the remaining portion.
 *   hold(1.0, 0.7, lerp(1.0, 0.0))  →  stays at 1.0 for 70% of life, then fades to 0
 */
export function hold(value: number, holdUntil: number, thenFn: PropertyFn<number>): PropertyFn<number> {
    return (elapsed, ttl) => {
        const t = elapsed / ttl;
        if (t < holdUntil) return value;
        const remainingTtl = ttl * (1 - holdUntil);
        const remainingElapsed = elapsed - ttl * holdUntil;
        return thenFn(remainingElapsed, remainingTtl);
    };
}

// ── Position helpers ──
// All return offset from origin in cell units. Velocity is in cells/sec.

/**
 * Constant-velocity straight-line motion.
 *   linearMotion(2, 0)  →  moves 2 cells/sec rightward (1 cell after 500ms)
 */
export function linearMotion(vx: number, vy: number): PropertyFn<{ x: number; y: number }> {
    return (elapsed) => {
        const t = elapsed / 1000;
        return { x: vx * t, y: vy * t };
    };
}

/**
 * Parabolic arc with gravity. Useful for debris, sparks, shrapnel.
 * Gravity is in cells/sec^2 (positive = downward).
 *   parabolic(2, -3, 6)  →  launches up-right, arcs down under gravity
 *   parabolic(0, 0, 10)  →  drops straight down (pure gravity)
 */
export function parabolic(vx: number, vy: number, gravity: number): PropertyFn<{ x: number; y: number }> {
    return (elapsed) => {
        const t = elapsed / 1000;
        return { x: vx * t, y: vy * t + 0.5 * gravity * t * t };
    };
}

/**
 * Motion that decelerates exponentially (drag). Starts at full speed and
 * asymptotically approaches a maximum distance of v/drag cells.
 * Higher drag = stops sooner. Good for exhaust, smoke, dust.
 *   deceleratingMotion(3, 0, 2.0)  →  shoots right, decelerates, stops ~1.5 cells away
 */
export function deceleratingMotion(vx: number, vy: number, drag: number): PropertyFn<{ x: number; y: number }> {
    return (elapsed) => {
        const t = elapsed / 1000;
        const factor = (1 - Math.exp(-drag * t)) / drag;
        return { x: vx * factor, y: vy * factor };
    };
}

/**
 * Evaluates a property function until `stopAt` fraction of lifetime, then
 * freezes at the value reached at that moment. Like `hold()` but freezes
 * the output of an existing function instead of holding a fixed value.
 * Works with any property type (position, rotation, size, etc.).
 *   stopAfter(parabolic(2, -1, 6), 0.4)  →  arcs for 40% of life, then stays where it landed
 *   stopAfter(lerp(0, 3), 0.4)  →  rotates for 40% of life, then stops
 */
export function stopAfter<T>(fn: PropertyFn<T>, stopAt: number): PropertyFn<T> {
    return (elapsed, ttl) => {
        if (elapsed / ttl < stopAt) return fn(elapsed, ttl);
        return fn(stopAt * ttl, ttl);
    };
}

// ── Color helpers ──

export type RGBColor = { r: number; g: number; b: number };

/**
 * Fixed color for the entire lifetime.
 *   colorConstant(255, 0, 0)  →  always red
 */
export function colorConstant(r: number, g: number, b: number): PropertyFn<RGBColor> {
    return () => ({ r, g, b });
}

/**
 * Linear interpolation between two colors over the particle's lifetime.
 *   colorLerp({r:255,g:0,b:0}, {r:0,g:0,b:0})  →  red fading to black
 */
export function colorLerp(a: RGBColor, b: RGBColor): PropertyFn<RGBColor> {
    return (elapsed, ttl) => {
        const t = elapsed / ttl;
        return {
            r: a.r + (b.r - a.r) * t,
            g: a.g + (b.g - a.g) * t,
            b: a.b + (b.b - a.b) * t,
        };
    };
}

/**
 * Multi-stop color gradient, evenly spaced over the lifetime. Needs at least 2 stops.
 *   colorGradient([yellow, orange, gray])  →  yellow at birth, orange at 50%, gray at death
 */
export function colorGradient(stops: RGBColor[]): PropertyFn<RGBColor> {
    return (elapsed, ttl) => {
        const t = elapsed / ttl;
        const segments = stops.length - 1;
        const scaledT = t * segments;
        const i = Math.min(Math.floor(scaledT), segments - 1);
        const localT = scaledT - i;
        const a = stops[i];
        const b = stops[i + 1];
        return {
            r: a.r + (b.r - a.r) * localT,
            g: a.g + (b.g - a.g) * localT,
            b: a.b + (b.b - a.b) * localT,
        };
    };
}

// ── Randomization utilities ──
// These are plain values (not PropertyFn). Call them inside preset factories
// to bake a random value per particle.

/** Random number in [min, max). */
export function randomBetween(min: number, max: number): number {
    return min + Math.random() * (max - min);
}

/** Returns -1 or 1 with equal probability. */
export function randomSign(): number {
    return Math.random() < 0.5 ? -1 : 1;
}

/** Random angle in [0, 2*PI). */
export function randomAngle(): number {
    return Math.random() * 2 * Math.PI;
}
